import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// OperatorQuickCreateForm.jsx
//
// Alta rápida de operador desde el panel del vehículo. Se muestra EN LÍNEA
// dentro del panel (no abre otro modal encima del popover, que es justo lo que
// hacía que el diálogo se descolocara).
//
// Al guardar, el backend crea y asigna en una sola operación.

import React, { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, FormControlLabel, Grid, InputAdornment,
  Stack, TextField, Typography,
} from '@mui/material';
import { ArrowBack as BackIcon, Save as SaveIcon } from '@mui/icons-material';

import { isValidPhone } from '../../utils/phone';

// Códigos usados en la operación; el usuario puede escribir otro.
const COUNTRY_CODES = ['+52', '+1', '+34', '+57', '+51', '+56'];

const OperatorQuickCreateForm = ({
  roleLabel, onCancel, onSubmit, onCreateOnly = null, saving = false, error = null,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    countryCode: '+52',
    name: '',
    last_name: '',
    phone: '',
    email: '',
    license_number: '',
    license_expires_at: '',
    is_active: true,
  });
  const [touched, setTouched] = useState(false);

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const nameError = touched && !form.name.trim();
  const phoneError = touched && form.phone.trim() && !isValidPhone(`${form.countryCode}${form.phone}`);
  const valid = form.name.trim() && !phoneError;

  // `handler` permite reutilizar la validacion para las dos salidas del
  // formulario: crear a secas o crear y asignar.
  const submit = (handler = onSubmit) => {
    setTouched(true);
    if (!form.name.trim()) return;
    if (form.phone.trim() && !isValidPhone(`${form.countryCode}${form.phone}`)) return;
    const { countryCode, phone, license_expires_at: expires, ...rest } = form;
    handler({
      ...rest,
      // El código de país se guarda junto al número, en un solo campo.
      phone: phone.trim() ? `${countryCode}${phone.replace(/\D/g, '')}` : null,
      license_expires_at: expires || null,
    });
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
        <Button size="small" startIcon={<BackIcon />} onClick={onCancel} sx={{ textTransform: 'none' }}>
          {i18n.t('hardcoded.components_operators_operatorquickcreateform.volver')}</Button>
        <Typography variant="subtitle2" fontWeight={700}>
          {t('hardcoded.components_operators_operatorquickcreateform.nuevo_operador')}{roleLabel ? ` · ${roleLabel}` : ''}
        </Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Grid container spacing={1.25}>
        <Grid item xs={12} sm={6}>
          <TextField
            size="small" fullWidth required autoFocus label="Nombre"
            value={form.name} onChange={set('name')}
            error={nameError} helperText={nameError ? t('hardcoded.components_operators_operatorquickcreateform.el_nombre_es_obligatorio') : ' '}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField size="small" fullWidth label={t('createUser.form.lastName')} value={form.last_name}
            onChange={set('last_name')} helperText=" " />
        </Grid>

        <Grid item xs={4} sm={3}>
          <TextField
            size="small" fullWidth select SelectProps={{ native: true }}
            label={t('settings.company.country')} value={form.countryCode} onChange={set('countryCode')} helperText=" "
          >
            {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
          </TextField>
        </Grid>
        <Grid item xs={8} sm={9}>
          <TextField
            size="small" fullWidth label={t('settings.company.phone')} value={form.phone} onChange={set('phone')}
            error={Boolean(phoneError)}
            helperText={phoneError ? t('hardcoded.components_operators_operatorquickcreateform.numero_no_valido_para_ese') : t('hardcoded.components_operators_operatorquickcreateform.para_llamar_o_enviar_whatsapp')}
            InputProps={{ startAdornment: <InputAdornment position="start">{form.countryCode}</InputAdornment> }}
          />
        </Grid>

                <Grid item xs={12} sm={6}>
          <TextField size="small" fullWidth label={t('hardcoded.components_operators_operatorquickcreateform.numero_de_licencia')} value={form.license_number}
            onChange={set('license_number')} helperText=" " />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            size="small" fullWidth type="date" label={t('hardcoded.components_operators_operatorquickcreateform.vence_la_licencia')}
            value={form.license_expires_at} onChange={set('license_expires_at')}
            InputLabelProps={{ shrink: true }} helperText=" "
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField size="small" fullWidth label={t('hardcoded.components_operators_operatorquickcreateform.email_opcional')} value={form.email}
            onChange={set('email')} helperText=" " />
        </Grid>

        
        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox size="small" checked={form.is_active} onChange={set('is_active')} />}
            label={<Typography variant="body2">{t('hardcoded.components_operators_operatorquickcreateform.operador_activo')}</Typography>}
          />
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mt: 1, mb: 1.5, py: 0.25 }}>
        <Typography variant="caption">
          {t('hardcoded.components_operators_operatorquickcreateform.este_operador_no_tendra_acceso')}</Typography>
      </Alert>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button size="small" onClick={onCancel} disabled={saving} sx={{ textTransform: 'none' }}>
          {t('common.cancel')}</Button>
        {/* "Crear" da de alta al operador sin asignarlo a esta unidad. */}
        {onCreateOnly && (
          <Button
            size="small" variant="outlined" startIcon={<SaveIcon />}
            onClick={() => submit(onCreateOnly)} disabled={saving || (touched && !valid)}
            sx={{ textTransform: 'none' }}
          >
            {t('audit.actions.CREATE')}
          </Button>
        )}
        <Button
          size="small" variant="contained" startIcon={<SaveIcon />}
          onClick={() => submit()} disabled={saving || (touched && !valid)}
          sx={{ textTransform: 'none' }}
        >
          {saving ? t('hardcoded.components_operators_operatorquickcreateform.guardando') : t('hardcoded.components_operators_operatorquickcreateform.crear_y_asignar')}
        </Button>
      </Stack>
    </Box>
  );
};

export default OperatorQuickCreateForm;
